import { isNil } from 'lodash'
import React, { ChangeEvent, FunctionComponent, useCallback } from 'react'

import { TextInput, TextInputProps } from '../atoms'
import { useNotifyingState } from '../extensions'

export const parser = (value: string): number | undefined => {
  const sanitisedValue = value.replace(/[^0-9]/g, '')
  if (sanitisedValue.length === 0) {
    return undefined
  }
  const num = Number(sanitisedValue)
  return Number.isInteger(num) ? num : undefined
}

export interface NumberInputProps extends Omit<TextInputProps, 'onChange'> {
  inputPlaceholder: string
  onChange: (value: number | undefined) => void
  formatter: (value: number) => string
  initialValue?: number
  maxValue?: number | undefined
}

export const NumberInput: FunctionComponent<NumberInputProps> = ({
  initialValue,
  inputPlaceholder,
  formatter,
  onChange,
  maxValue,
  ...props
}) => {
  const [value, setValue] = useNotifyingState(initialValue, [onChange])

  const onValueChanged = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const valueAsNumber = parser(target.value)
      if (
        isNil(valueAsNumber) ||
        isNil(maxValue) ||
        (!isNil(valueAsNumber) && valueAsNumber <= maxValue)
      ) {
        setValue(valueAsNumber)
      }
    },
    [setValue, maxValue],
  )

  const format = (num: number | undefined) => (isNil(num) ? '' : formatter(num))

  return (
    <TextInput
      type='tel'
      inputMode='decimal'
      id='number'
      name='number'
      placeholder={inputPlaceholder}
      value={format(value)}
      onChange={onValueChanged}
      {...props}
    />
  )
}
